import { derived, get } from "svelte/store";
import { getDistance, getRhumbLineBearing, getCompassDirection } from "geolib";
import profile from "./profile.js";
import chronology from "./chronology.js";
import route from "./route.js";
import semaphore from "./semaphore.js";

const currentLocation = (() => {
	let duration = 0;
	let index = 0;
	let heading = 0;
	let previousRoute;

	return ($chronology, route, $semaphore) => {
		const didChangeRoute =
			previousRoute &&
			(route.length !== previousRoute.length ||
				route[0].latitude !== previousRoute[0].latitude ||
				route[0].longitude !== previousRoute[0].longitude);

		previousRoute = route;

		let chronologyDuration = $chronology.duration;

		if (didChangeRoute) {
			chronology.reset();
			chronologyDuration = 0;
			duration = 0;
			index = 0;
		}

		while (index < route.length - 2) {
			const nextDuration = duration + route[index].duration;

			if (nextDuration <= chronologyDuration) {
				duration += route[index].duration;
				index += 1;
			} else {
				break;
			}
		}

		index = Math.max(Math.min(index, route.length - 2), 0);

		const a = route[index];
		const b = route[index + 1];

		const progress =
			a.duration === 0.0 ? 1.0 : Math.max(Math.min((chronologyDuration - duration) / a.duration, 1.0), 0.0);

		const nextHeading = getRhumbLineBearing(a, b);
		const delta = ((((nextHeading - heading) % 360) + 540) % 360) - 180;
		heading += delta;

		const compass = getCompassDirection(a, b);
		const selectedProfile = get(profile);
		const speed = (getDistance(a, b) / a.duration) * 1_000;

		const location = {
			latitude: a.latitude + (b.latitude - a.latitude) * progress,
			longitude: a.longitude + (b.longitude - a.longitude) * progress,
			heading,
			compass,
			speed: speed * (selectedProfile ? selectedProfile.vehicle.speed : 1.0),
			index
		};

		return {
			location,
			isEnded: index === route.length - 2 && progress === 1.0
		};
	};
})();

let previousLocation;

const journey = derived([chronology, route, semaphore], ([$chronology, $route, $semaphore]) => {
	if ($semaphore.isPaused && previousLocation !== undefined) {
		return previousLocation;
	}

	if ($route) {
		const { location, isEnded } = currentLocation($chronology, $route);

		if (isEnded) {
			const end = {
				...location,
				...$route.at(-1)
			};

			const result = {
				location: end,
				distance: 0.0,
				isEnded: true
			};

			previousLocation = result;

			return result;
		}

		const distance = previousLocation ? getDistance(location, previousLocation) : 0.0;

		const result = {
			location,
			distance,
			isEnded: false
		};

		previousLocation = result;

		return result;
	}

	return {
		location: null,
		distance: 0.0,
		isEnded: false
	};
});

export function journeySubscribeThrottled(interval, callback) {
	let lastNotifiedAt = Number.MIN_SAFE_INTEGER;
	let timer;

	return journey.subscribe((state) => {
		clearTimeout(timer);

		const shouldNotifyNow = Date.now() - lastNotifiedAt > interval;

		const notify = () => {
			callback(state);
			lastNotifiedAt = Date.now();
		};

		if (shouldNotifyNow) {
			notify();
		} else {
			timer = setTimeout(notify, interval);
		}
	});
}

export default journey;
